import React, { useState, useEffect } from 'react';
import { itemsAPI, groceriesAPI } from '../services/api';
import ItemForm from './ItemForm';
import './ItemList.css';

const ItemList = ({ houseId, categoryId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'

  useEffect(() => {
    if (houseId && categoryId) {
      loadItems();
    } else {
      setItems([]);
      setLoading(false);
    }
  }, [houseId, categoryId]);

  const loadItems = async () => {
    try {
      const response = await itemsAPI.getAll(houseId, categoryId);
      setItems(response.data);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToGroceries = async (item) => {
    try {
      await groceriesAPI.create({
        house_id: houseId,
        name: item.name,
        quantity: item.quantity > 0 ? `${item.quantity} ${item.unit || ''}`.trim() : null,
        notes: item.notes || null,
      });
      alert(`${item.name} added to grocery list!`);
    } catch (error) {
      console.error('Error adding to groceries:', error);
      alert('Failed to add item to grocery list');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await itemsAPI.delete(id);
        loadItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingItem(null);
    loadItems();
  };

  const getStockStatus = (item) => {
    if (item.low_stock_threshold && item.quantity <= item.low_stock_threshold) {
      return 'low';
    }
    if (item.quantity === 0) {
      return 'out';
    }
    return 'ok';
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return <div className="item-list-loading">Loading items...</div>;
  }

  if (!houseId || !categoryId) {
    return <div className="item-list-message">Please select a house and category</div>;
  }

  return (
    <div className="item-list">
      <div className="item-list-header">
        <h2>Items</h2>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              ⊞
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              ☰
            </button>
          </div>
          <button className="btn-add" onClick={() => setShowForm(true)}>
            + Add Item
          </button>
        </div>
      </div>

      {showForm && (
        <ItemForm
          houseId={houseId}
          categoryId={categoryId}
          item={editingItem}
          onClose={handleFormClose}
        />
      )}

      {items.length === 0 ? (
        <div className="item-list-empty">No items found. Add your first item!</div>
      ) : (
        <div className={viewMode === 'grid' ? 'items-grid' : 'items-list'}>
          {items.map((item) => {
            const status = getStockStatus(item);
            const daysUntilExpiry = getDaysUntilExpiry(item.expiry_date);

            return (
              <div key={item.id} className={`item-card status-${status} view-${viewMode}`}>
                <div className="item-card-main">
                  <div className="item-info">
                    <h3 className="item-name">{item.name}</h3>
                    <div className="item-meta">
                      {item.quantity > 0 && (
                        <span className="item-quantity-badge">
                          {item.quantity}{item.unit ? ` ${item.unit}` : ''}
                        </span>
                      )}
                      {item.expiry_date && daysUntilExpiry !== null && (
                        <span className={`expiry-badge ${daysUntilExpiry <= 7 ? 'urgent' : ''}`}>
                          {daysUntilExpiry}d
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="item-actions-minimal">
                    <button 
                      className="btn-add-grocery" 
                      onClick={() => handleAddToGroceries(item)}
                      title="Add to Groceries"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                      </svg>
                    </button>
                    <button 
                      className="btn-edit-minimal" 
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button 
                      className="btn-delete-minimal" 
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ItemList;

